import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CourseWithLessons, UserProgress } from '../services/courseService';
import { mockCourseService } from '../services/mockData';
import { MOCK_INSTRUCTORS, MOCK_REVIEWS } from '../services/mockDataExpanded';

export const CourseDetailPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<CourseWithLessons | null>(null);
  const [progress, setProgress] = useState<UserProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCourse = async () => {
      if (!courseId) return;
      
      try {
        setLoading(true);
        const courseData = await mockCourseService.getCourse(courseId);
        setCourse(courseData);
        
        // Try to get progress if user is enrolled
        try {
          const progressData = await mockCourseService.getCourseProgress(courseId);
          setProgress(progressData);
        } catch {
          // User not enrolled - that's fine
        }
      } catch (err) {
        setError('Course not found');
      } finally {
        setLoading(false);
      }
    };

    loadCourse();
  }, [courseId]);

  const handleEnroll = async () => {
    if (!courseId) return;
    
    setEnrolling(true);
    try {
      const newProgress = await mockCourseService.enrollInCourse(courseId);
      setProgress(newProgress);
      // Redirect to first lesson
      navigate(`/courses/${courseId}/lessons/${course?.lessons[0]?.id}`);
    } catch (err) {
      setError('Failed to enroll in course');
    } finally {
      setEnrolling(false);
    }
  };

  const handleContinue = () => {
    if (!course || !progress) return;
    
    if (progress.current_lesson_id) {
      navigate(`/courses/${courseId}/lessons/${progress.current_lesson_id}`);
    } else {
      navigate(`/courses/${courseId}/lessons/${course.lessons[0]?.id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{error || 'Course not found'}</h1>
          <Link to="/courses" className="text-indigo-600 hover:text-indigo-500">
            ← Back to Courses
          </Link>
        </div>
      </div>
    );
  }

  const instructor = MOCK_INSTRUCTORS[courseId as keyof typeof MOCK_INSTRUCTORS];
  const reviews = MOCK_REVIEWS[courseId as keyof typeof MOCK_REVIEWS] || [];
  const isEnrolled = !!progress;

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumbs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <nav className="text-sm text-gray-600">
            <Link to="/" className="hover:text-indigo-600">Home</Link>
            <span className="mx-2">→</span>
            <Link to="/courses" className="hover:text-indigo-600">Courses</Link>
            <span className="mx-2">→</span>
            <span className="text-gray-900">{course.title}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Hero Section */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">{course.title}</h1>
                  <p className="text-lg text-gray-600 mb-6">{course.description}</p>
                  
                  <div className="flex items-center space-x-6 text-sm text-gray-500">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(course.difficulty_level)}`}>
                      {course.difficulty_level.charAt(0).toUpperCase() + course.difficulty_level.slice(1)}
                    </span>
                    <div className="flex items-center">
                      <span className="text-yellow-400 mr-1">⭐</span>
                      <span className="font-medium">{course.average_rating}</span>
                      <span className="ml-1">({reviews.length} reviews)</span>
                    </div>
                    <div className="flex items-center">
                      <span className="mr-1">👥</span>
                      <span>{course.enrollment_count.toLocaleString()} students</span>
                    </div>
                    <div className="flex items-center">
                      <span className="mr-1">🕐</span>
                      <span>{course.estimated_duration_hours} hours</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="mb-6">
                {isEnrolled ? (
                  <div className="space-y-3">
                    <button
                      onClick={handleContinue}
                      className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
                    >
                      Continue Learning
                    </button>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                        <span>Progress</span>
                        <span>{progress.completion_percentage}% Complete</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress.completion_percentage}%` }}
                        ></div>
                      </div>
                      <div className="mt-2 text-sm text-gray-500">
                        {progress.completed_lessons} of {progress.total_lessons} lessons completed
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={handleEnroll}
                    disabled={enrolling}
                    className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  >
                    {enrolling ? 'Enrolling...' : course.is_free ? 'Enroll for Free' : 'Enroll Now'}
                  </button>
                )}
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {course.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-sm"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Learning Objectives */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">What You'll Learn</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {course.learning_objectives.map((objective, index) => (
                  <div key={index} className="flex items-start">
                    <span className="text-green-500 mr-3 mt-1">✓</span>
                    <span className="text-gray-700">{objective}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Prerequisites */}
            {course.prerequisites.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Prerequisites</h2>
                <div className="space-y-2">
                  {course.prerequisites.map((prereq, index) => (
                    <div key={index} className="flex items-center">
                      <span className="text-blue-500 mr-3">📚</span>
                      <span className="text-gray-700">{prereq}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Course Content */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Course Content</h2>
              <div className="space-y-4">
                {course.lessons.map((lesson, index) => {
                  const isCompleted = progress && index < progress.completed_lessons;
                  const isCurrent = progress && lesson.id === progress.current_lesson_id;
                  const isAccessible = lesson.is_preview || isEnrolled;
                  
                  return (
                    <div
                      key={lesson.id}
                      className={`border rounded-lg p-4 transition-colors ${
                        isCurrent ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                            isCompleted 
                              ? 'bg-green-100 text-green-600' 
                              : isCurrent 
                              ? 'bg-indigo-100 text-indigo-600'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {isCompleted ? '✓' : index + 1}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">{lesson.title}</h3>
                            <p className="text-sm text-gray-600">{lesson.description}</p>
                            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                              <span className="flex items-center">
                                <span className="mr-1">🎯</span>
                                {lesson.lesson_type}
                              </span>
                              <span className="flex items-center">
                                <span className="mr-1">⏱️</span>
                                {lesson.estimated_duration_minutes} min
                              </span>
                              <span className="flex items-center">
                                <span className="mr-1">⭐</span>
                                {lesson.points_value} points
                              </span>
                              {lesson.is_preview && (
                                <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded text-xs">
                                  Free Preview
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {isAccessible && (
                          <Link
                            to={`/courses/${courseId}/lessons/${lesson.id}`}
                            className="text-indigo-600 hover:text-indigo-500 font-medium"
                          >
                            {lesson.is_preview ? 'Preview' : 'Start'}
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Reviews */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Student Reviews</h2>
              <div className="space-y-6">
                {reviews.map((review) => (
                  <div key={review.id} className="border-b border-gray-100 pb-6 last:border-b-0 last:pb-0">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">{review.user_name}</h4>
                        <div className="flex items-center mt-1">
                          <div className="flex text-yellow-400">
                            {Array.from({ length: 5 }, (_, i) => (
                              <span key={i}>{i < review.rating ? '⭐' : '☆'}</span>
                            ))}
                          </div>
                          <span className="text-sm text-gray-500 ml-2">{review.date}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-2">{review.comment}</p>
                    <div className="text-sm text-gray-500">
                      {review.helpful_count} people found this helpful
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Instructor Info */}
            {instructor && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Your Instructor</h3>
                <div className="text-center">
                  <div className="w-16 h-16 bg-indigo-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <span className="text-2xl font-bold text-indigo-600">
                      {instructor.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <h4 className="font-bold text-gray-900">{instructor.name}</h4>
                  <p className="text-sm text-gray-600 mb-3">{instructor.title}</p>
                  <p className="text-sm text-gray-700 mb-4">{instructor.bio}</p>
                  
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-indigo-600">{instructor.courses_taught}</div>
                      <div className="text-sm text-gray-600">Courses</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-indigo-600">{instructor.student_count.toLocaleString()}</div>
                      <div className="text-sm text-gray-600">Students</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-center mt-4">
                    <span className="text-yellow-400 mr-1">⭐</span>
                    <span className="font-medium">{instructor.rating}</span>
                    <span className="text-gray-500 ml-1">instructor rating</span>
                  </div>

                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-gray-900 mb-2">Expertise</h5>
                    <div className="flex flex-wrap gap-1">
                      {instructor.expertise.map((skill) => (
                        <span
                          key={skill}
                          className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Course Stats */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Course Stats</h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Skill Level</span>
                  <span className="font-medium capitalize">{course.difficulty_level}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Students Enrolled</span>
                  <span className="font-medium">{course.enrollment_count.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Languages</span>
                  <span className="font-medium">English</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Updated</span>
                  <span className="font-medium">{new Date(course.updated_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {/* Certificate */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-sm p-6 text-white">
              <h3 className="text-lg font-bold mb-2">🏆 Certificate of Completion</h3>
              <p className="text-indigo-100 text-sm mb-4">
                Earn a certificate when you complete this course to showcase your new skills.
              </p>
              <div className="text-xs text-indigo-200">
                Shareable on LinkedIn, resume, and portfolio
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};